import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowLeft, MessageCircle, CheckCircle2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, WHATSAPP_LINK } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { StatusBadge } from "@/components/ProjectCard";
import { resolveImageUrl } from "@/components/ImageUploader";
import { useSiteContent } from "@/context/SiteContentContext";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { content } = useSiteContent();
  const t = content.project_detail;
  const wnum = content.contact.whatsapp_number;
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    api
      .get(`/projects/${slug}`)
      .then((r) => setProject(r.data))
      .catch(() => setError("Proje bulunamadı"));
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % project.gallery.length);
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + project.gallery.length) % project.gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, project]);

  if (error) {
    return (
      <div className="pt-40 pb-24 text-center bg-[var(--ivory)] min-h-screen">
        <h1 className="font-serif-luxe text-3xl text-[var(--navy)]">{error}</h1>
        <Link to="/projeler" className="btn-primary mt-8 inline-flex">
          <ArrowLeft size={16} /> Projelere Dön
        </Link>
      </div>
    );
  }

  if (!project) {
    return <div className="pt-40 pb-24 text-center bg-[var(--ivory)] min-h-screen text-[var(--slate)]">Yükleniyor…</div>;
  }

  return (
    <div className="bg-[var(--ivory)]" data-testid={`project-detail-${project.slug}`}>
      {/* HERO */}
      <section className="relative min-h-[90vh] w-full overflow-hidden">
        <img src={resolveImageUrl(project.hero_image)} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full border border-[var(--gold)]/15" />

        <div className="relative z-10 min-h-[90vh] flex items-end pb-20 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto w-full text-[var(--ivory)]">
            <Reveal>
              <Link to="/projeler" className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--ivory)]/80 hover:text-[var(--gold)]" data-testid="back-to-projects">
                <ArrowLeft size={14} /> {t.back_label}
              </Link>
            </Reveal>
            <Reveal delay={120}>
              <div className="mt-6 flex items-center gap-4 flex-wrap">
                <StatusBadge status={project.status} />
                <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[var(--ivory)]/85">
                  <MapPin size={14} className="text-[var(--gold)]" /> {project.location}
                </div>
              </div>
              <h1 className="mt-4 font-serif-luxe text-5xl lg:text-7xl leading-[1.05] max-w-4xl">
                {project.title}
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-6 max-w-xl text-base lg:text-lg text-[var(--ivory)]/85 font-light leading-relaxed">
                {project.short_description}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href={WHATSAPP_LINK(`Merhaba, ${project.title} projesi hakkında bilgi almak istiyorum.`, wnum)} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="detail-cta-whatsapp">
                  <MessageCircle size={16} /> {t.info_cta_label}
                </a>
                <Link to="/iletisim" className="btn-ghost-gold" style={{ color: "var(--ivory)" }} data-testid="detail-cta-contact">
                  {t.contact_cta_label}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DESCRIPTION + FEATURES */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="pointer-events-none absolute top-20 right-10 w-32 h-32 border border-[var(--gold)]/30 rounded-tl-full" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <span className="eyebrow">{t.about_eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 text-[var(--navy)] leading-tight">
              {t.about_heading_line1} <span className="italic text-[var(--gold)]">{t.about_heading_italic}</span>
            </h2>
            <div className="gold-divider w-24 my-6" />
            <p className="text-[var(--slate)] leading-relaxed font-light text-base lg:text-lg whitespace-pre-line">
              {project.description}
            </p>
          </Reveal>

          {project.features?.length > 0 && (
            <Reveal className="lg:col-span-5" delay={120}>
              <div className="bg-[var(--cream)] p-8 lg:p-10 border border-[var(--gold)]/25 relative">
                <span className="absolute top-3 right-3 w-7 h-7 border-t border-r border-[var(--gold)]" />
                <h3 className="font-serif-luxe text-2xl text-[var(--navy)] mb-6">{t.features_title}</h3>
                <ul className="space-y-3">
                  {project.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-[var(--ink)] font-light">
                      <CheckCircle2 size={16} className="mt-1 text-[var(--gold)] shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* SOCIAL AREAS + LOCATION */}
      <section className="relative bg-[var(--cream)] py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10">
          {project.social_areas?.length > 0 && (
            <Reveal>
              <div className="bg-[var(--navy)] text-[var(--ivory)] p-10 lg:p-12 relative overflow-hidden">
                <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full border border-[var(--gold)]/20" />
                <span className="eyebrow text-[var(--gold)]">{t.social_eyebrow}</span>
                <h3 className="font-serif-luxe text-3xl lg:text-4xl mt-3">{t.social_heading}</h3>
                <div className="gold-divider w-20 my-5" />
                <ul className="space-y-3">
                  {project.social_areas.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm font-light text-[var(--ivory)]/90">
                      <CheckCircle2 size={16} className="mt-1 text-[var(--gold)] shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}

          {project.location_advantages?.length > 0 && (
            <Reveal delay={120}>
              <div className="bg-[var(--ivory)] p-10 lg:p-12 border border-[var(--gold)]/30 relative">
                <span className="absolute bottom-3 left-3 w-7 h-7 border-b border-l border-[var(--gold)]" />
                <span className="eyebrow">{t.location_eyebrow}</span>
                <h3 className="font-serif-luxe text-3xl lg:text-4xl mt-3 text-[var(--navy)]">{t.location_heading}</h3>
                <div className="gold-divider w-20 my-5" />
                <ul className="space-y-3">
                  {project.location_advantages.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm font-light text-[var(--ink)]">
                      <MapPin size={16} className="mt-1 text-[var(--gold)] shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* GALLERY */}
      {project.gallery?.length > 0 && (
        <section className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
          <div className="max-w-7xl mx-auto mb-12">
            <Reveal>
              <span className="eyebrow">{t.gallery_eyebrow}</span>
              <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 text-[var(--navy)]">
                {t.gallery_heading_line1} <span className="italic text-[var(--gold)]">{t.gallery_heading_italic}</span>
              </h2>
            </Reveal>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {project.gallery.map((img, i) => (
              <Reveal key={img + i} delay={i * 80}>
                <button
                  onClick={() => setLightboxIndex(i)}
                  className={`group relative w-full overflow-hidden ${
                    i % 5 === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-[4/3]" : "aspect-[4/5]"
                  }`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={resolveImageUrl(img)} alt={`${project.title} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-[var(--navy)]/0 group-hover:bg-[var(--navy)]/30 transition-colors" />
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative bg-[var(--navy)] text-[var(--ivory)] py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-[var(--gold)]/15" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="eyebrow text-[var(--gold)]">{t.cta_eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3">
              {project.title} hakkında <span className="italic text-[var(--gold)]">{t.cta_heading_italic}</span>
            </h2>
            <p className="mt-6 text-[var(--ivory)]/80 font-light max-w-xl mx-auto leading-relaxed whitespace-pre-line">
              {t.cta_body}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <a href={WHATSAPP_LINK(`Merhaba, ${project.title} projesi hakkında bilgi almak istiyorum.`, wnum)} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="detail-bottom-whatsapp">
                <MessageCircle size={16} /> {t.cta_whatsapp_label}
              </a>
              <Link to="/iletisim" className="btn-ghost-gold" style={{ color: "var(--ivory)" }} data-testid="detail-bottom-contact">
                {t.cta_contact_label}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-[60] lightbox-backdrop flex items-center justify-center px-4" data-testid="lightbox">
          <button onClick={() => setLightboxIndex(null)} className="absolute top-6 right-6 text-[var(--ivory)] hover:text-[var(--gold)]" data-testid="lightbox-close">
            <X size={28} />
          </button>
          <button
            onClick={() => setLightboxIndex((i) => (i - 1 + project.gallery.length) % project.gallery.length)}
            className="absolute left-4 lg:left-10 text-[var(--ivory)] hover:text-[var(--gold)] p-3"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            onClick={() => setLightboxIndex((i) => (i + 1) % project.gallery.length)}
            className="absolute right-4 lg:right-10 text-[var(--ivory)] hover:text-[var(--gold)] p-3"
            data-testid="lightbox-next"
          >
            <ChevronRight size={36} />
          </button>
          <img
            src={resolveImageUrl(project.gallery[lightboxIndex])}
            alt={`${project.title} büyük görsel`}
            className="max-w-[92vw] max-h-[88vh] object-contain"
          />
        </div>
      )}
    </div>
  );
}
